<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  claimTaxYearConfig,
  useClaimTaxBack,
} from '@/composables/useClaimTaxBack'
import { useDonorFinancialYears } from '@/composables/useDonorFinancialYears'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import {
  giftAidedDonationsForPeriod,
  totalGivenPence,
} from '@/helpers/claimTaxBack/givenTotal'
import { isDonorAddedDonation } from '@/helpers/taxPackDonations'
import { hasResolvedPayMethod, resolveClaimRoute } from '@/helpers/claimTaxBack/claimRoute'
import {
  CLAIM_FUNNEL_EVENTS,
  trackClaimFunnel,
} from '@/helpers/claimTaxBack/funnel'
import { calculateClaimEstimateForBand } from '@/helpers/claimTaxBack/estimate'
import {
  bandQualifiesForExtraRelief,
  findIncomeBandById,
} from '@/helpers/claimTaxBack/taxYearConfig'
import DashboardDataLoading from '@/components/dashboard/ui/DashboardDataLoading.vue'
import ClaimGuideTaxStep from '@/components/dashboard/claim/ClaimGuideTaxStep.vue'
import ClaimGuideStepIndex from '@/components/dashboard/claim/ClaimGuideStepIndex.vue'
import ClaimGuideDonationsStep from '@/components/dashboard/claim/ClaimGuideDonationsStep.vue'
import ClaimRouteCard from '@/components/dashboard/claim/ClaimRouteCard.vue'
import ClaimClaimedSection from '@/components/dashboard/claim/ClaimClaimedSection.vue'
import ClaimFaq from '@/components/dashboard/claim/ClaimFaq.vue'

/**
 * Paid claim guide. Steps collapse to summaries as they complete, so a
 * returning donor lands on "here's your claim" rather than a form to redo:
 * 1. Tax situation (region, income band, pay method) - quick answers first.
 * 2. Build the claim from Gift Aided donations, with a live snapshot and an
 *    explicit confirmation that Gift Aided donations are complete.
 * 3. The HMRC route - the payoff, always open.
 * 4. Mark the year claimed once they have contacted HMRC (undoable).
 * When claimed, the whole year collapses to a summary so a returning donor
 * is not dropped back into the steps. They can expand the full guide again.
 * Title and the steps share one panel. Steps 2 and 3 stay pending
 * until step 1 is complete. Step 4 waits until donations are confirmed.
 * If step 1 shows they paid only basic-rate tax, the guide stops: there
 * is nothing extra to claim. Figures live in step 2 so they update as
 * donations are recorded. Returning donors still see a short summary
 * because step 2 collapses. FAQ sits outside the claim card so it stays
 * visible when the year is collapsed. It is hidden if they cannot claim.
 * Progress is a numbered rail that turns into an amber tick when a step
 * is done. The giving figure always comes from the account (never the
 * checker's typed figure), so everything recalculates when donations
 * are recorded.
 */
const { t } = useI18n()
const { profile } = useClaimTaxBack()
const { sortedYears, isReady: donationsReady } = useDonorFinancialYears()
const { manualDonations } = useManualGivingDonations()

// Manual donations start as [] and fill in; waiting on that flag left the
// guide spinning after a Vite HMR when the GET was skipped or failed.
const isGuideLoading = computed(() => !donationsReady.value)

const periodKey = claimTaxYearConfig.periodKey
const yearLabel = claimTaxYearConfig.yearLabel

// Claim-year giving from the account (Swiftaid rows + donor-added rows).
const claimYearDonations = computed(() => {
  const swiftaidRows =
    sortedYears.value.find((year) => year.period === periodKey)?.donations ?? []
  const manualRows = manualDonations.value.filter(isDonorAddedDonation)
  return giftAidedDonationsForPeriod(
    [...swiftaidRows, ...manualRows],
    periodKey
  )
})

const totalGiven = computed(() => totalGivenPence(claimYearDonations.value))
const donationCount = computed(() => claimYearDonations.value.length)

const savedBand = computed(() => {
  const bandId = profile.value?.incomeBandId
  if (!bandId) return null
  return findIncomeBandById(claimTaxYearConfig, bandId)
})

const qualifiesForExtraRelief = computed(() =>
  savedBand.value
    ? bandQualifiesForExtraRelief(savedBand.value, claimTaxYearConfig)
    : false
)

const isTaxComplete = computed(() => {
  if (!profile.value?.region || !savedBand.value) return false
  if (!qualifiesForExtraRelief.value) return true
  const method = profile.value.payMethod
  return hasResolvedPayMethod(method)
})

const showNothingOwed = computed(
  () => isTaxComplete.value && !qualifiesForExtraRelief.value
)

const isDonationsConfirmed = computed(
  () => !!profile.value?.donationsConfirmedAt
)

const route = computed(() =>
  resolveClaimRoute(
    profile.value?.payMethod ?? null,
    totalGiven.value,
    claimTaxYearConfig
  )
)

const showClaimedPrompt = computed(
  () =>
    isTaxComplete.value &&
    qualifiesForExtraRelief.value &&
    isDonationsConfirmed.value &&
    totalGiven.value > 0 &&
    route.value.id !== 'unknown'
)

const isClaimed = computed(() => Boolean(profile.value?.claimed))
const isYearExpanded = ref(!isClaimed.value)
const hideStepsBtn = ref<HTMLButtonElement | null>(null)

watch(isClaimed, (claimed) => {
  isYearExpanded.value = !claimed
})

const showYearSummary = computed(
  () => isClaimed.value && !isYearExpanded.value
)

const claimablePence = computed(() => {
  const bandId = profile.value?.incomeBandId
  if (!bandId) return null
  return calculateClaimEstimateForBand(
    totalGiven.value,
    bandId,
    claimTaxYearConfig
  ).claimablePence
})

const expandYear = async () => {
  isYearExpanded.value = true
  await nextTick()
  hideStepsBtn.value?.focus()
}

const collapseYear = () => {
  isYearExpanded.value = false
}

onMounted(() => {
  trackClaimFunnel(CLAIM_FUNNEL_EVENTS.GuideView, {
    period_key: periodKey,
  })
})
</script>

<template>
  <DashboardDataLoading v-if="isGuideLoading" />
  <div v-else class="sa-claim-guide">
    <section
      class="sa-card sa-card--padded sa-claim-card sa-claim-guide__panel"
      aria-labelledby="claim-guide-heading"
    >
      <header class="sa-claim-guide__header">
        <div class="sa-claim-guide__header-row">
          <h2
            id="claim-guide-heading"
            class="sa-claim-card__title"
            tabindex="-1"
          >
            {{ t('Dashboard.ClaimTaxBack.Guide.Title', { year: yearLabel }) }}
          </h2>
          <button
            v-if="isClaimed && isYearExpanded"
            ref="hideStepsBtn"
            type="button"
            class="sa-claim-link-btn sa-claim-guide__step-edit"
            :aria-expanded="true"
            aria-controls="claim-guide-year-body"
            @click="collapseYear"
          >
            {{ t('Dashboard.ClaimTaxBack.Guide.Claimed.Collapse') }}
          </button>
        </div>
        <p class="sa-claim-card__intro">
          {{
            isClaimed
              ? t('Dashboard.ClaimTaxBack.Guide.Claimed.CollapsedIntro', {
                  year: yearLabel,
                })
              : t('Dashboard.ClaimTaxBack.Guide.Intro', { year: yearLabel })
          }}
        </p>
      </header>

      <div id="claim-guide-year-body">
        <ClaimClaimedSection
          v-if="showYearSummary"
          variant="year"
          :total-given-pence="totalGiven"
          :donation-count="donationCount"
          :claimable-pence="claimablePence"
          @expand="expandYear"
        />

        <template v-else>
          <ClaimGuideTaxStep :step-number="1" />

          <section
            v-if="showNothingOwed"
            class="sa-claim-guide__step"
            aria-labelledby="claim-guide-nothing-owed-heading"
          >
            <div class="sa-claim-guide__step-header">
              <ClaimGuideStepIndex quiet />
              <h3
                id="claim-guide-nothing-owed-heading"
                class="sa-claim-guide__step-title"
              >
                {{ t('Dashboard.ClaimTaxBack.Guide.NothingOwed.Title') }}
              </h3>
            </div>
            <div class="sa-claim-guide__step-main">
              <p class="sa-claim-guide__step-body">
                {{
                  t('Dashboard.ClaimTaxBack.Guide.NothingOwed.Body', {
                    year: yearLabel,
                  })
                }}
              </p>
            </div>
          </section>

          <template v-else>
            <ClaimGuideDonationsStep
              :step-number="2"
              :total-given-pence="totalGiven"
              :donation-count="donationCount"
              :tax-ready="isTaxComplete"
            />

            <ClaimRouteCard
              :route="route"
              :total-given-pence="totalGiven"
              :donations-confirmed="isDonationsConfirmed"
              :donations="claimYearDonations"
            />

            <Transition name="sa-claim-fade">
              <ClaimClaimedSection v-if="showClaimedPrompt" />
            </Transition>
          </template>
        </template>
      </div>
    </section>

    <ClaimFaq v-if="!showNothingOwed" />
  </div>
</template>
